import Image from '@atoms/Image/Image';
import NavButton from '@atoms/NavButton/NavButton';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import Tag from '@atoms/Tag/Tag';
import {
  ITabbedMediaCarousel,
  ImageCard,
  VideoCard,
} from '@models/ITabbedMediaCarousel';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import TagTypesEnum from '@models/enums/TagTypesEnum';
import Carousel from '@molecules/Carousel/Carousel';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import duplicateArrayItems from '@utils/helpers/duplicateArrayItems';
import updateDataLayer from '@utils/helpers/gtm';
import slugify from '@utils/helpers/slugify';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swiper from 'swiper';
import { SwiperSlide } from 'swiper/react';

const TabbedMediaCarousel = ({
  anchorId = '',
  tabs = [],
}: ITabbedMediaCarousel) => {
  const carouselWrapperRef = useRef<HTMLDivElement>(null);
  const [swiperInstance, setSwiperInstance] = useState<Swiper>();
  const isMobile = useMediaQuery(768);
  const viewportBreakpoint = useBreakpoint();
  const screenBreakpoint = useBreakpoint(BreakpointTypeEnum.Screen);
  const [tabsContainerRef, { width: tabsContainerWidth }] = useResizeObserver();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [slideIndex, setSlideIndex] = useState(0);
  const [mediaHeight, setMediaHeight] = useState(0);
  const [mediaLeftOffset, setMediaLeftOffset] = useState(0);
  const [isSwiperInitialized, setIsSwiperInitialized] = useState(false);
  const tabTitles = tabs.map((tab) => tab.title);
  const navButtonId = `tabbed-media-carousel`;
  const imageSlideTestId = 'CT-135-Tabbed-Media-Carousel-Card-Image';
  const videoSlideTestId = 'CT-135-Tabbed-Media-Carousel-Card-Video';
  const activeCarousel = useMemo(
    () => [...tabs[activeTabIndex]!.cards],
    [tabs, activeTabIndex]
  );
  const isZoomedDesktop =
    screenBreakpoint === BreakpointEnum.XLarge &&
    viewportBreakpoint !== BreakpointEnum.XLarge;
  const shouldLoop =
    !!screenBreakpoint &&
    screenBreakpoint !== BreakpointEnum.Small &&
    activeCarousel.length > 1;
  const duplicatedItems = duplicateArrayItems(
    activeCarousel,
    activeCarousel.length <= 2 ? 6 : activeCarousel.length + 1
  );
  const itemsToDisplay = shouldLoop ? duplicatedItems : activeCarousel;
  const isNavButtonDisplayed =
    screenBreakpoint === BreakpointEnum.XLarge && itemsToDisplay.length > 1;

  const onResize = useCallback(() => {
    const activeImage = document.querySelector(
      `.swiper-slide-active [data-testid^="${imageSlideTestId}"]`
    );
    const activeVideo = document.querySelector(
      `.swiper-slide-active [data-testid^="${videoSlideTestId}"]`
    );
    const activeMedia = activeImage || activeVideo;

    if (!activeMedia) return;

    const { height, width } = activeMedia.getBoundingClientRect();
    const leftOffset = (document.body.clientWidth - width) / 2;

    setMediaHeight(height);
    setMediaLeftOffset(leftOffset);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    // We need to re-initialize the navigation when we're zooming as the nav buttons position is changed
    swiperInstance?.navigation.init();
  }, [isZoomedDesktop]);

  return (
    <section
      id={`a-${anchorId}`}
      className="component-padding relative overflow-hidden bg-white text-black-100 dark:border-black-100 dark:bg-black-100 dark:text-white"
      data-testid="CT-135-Tabbed-Media-Carousel"
    >
      {isZoomedDesktop && isNavButtonDisplayed && (
        <div className="relative h-20">
          <div
            className="absolute flex gap-x-[25px]"
            style={{ right: mediaLeftOffset }}
          >
            <NavButton
              direction={HorizontalDirectionEnum.Left}
              id={navButtonId}
              disabled={!shouldLoop}
            />
            <NavButton
              direction={HorizontalDirectionEnum.Right}
              id={navButtonId}
              disabled={!shouldLoop}
            />
          </div>
        </div>
      )}

      {!!itemsToDisplay?.length && (
        <div className="relative w-full" ref={carouselWrapperRef}>
          <ShadowContainerHorizontal
            showStart={
              viewportBreakpoint !== BreakpointEnum.XLarge &&
              (shouldLoop || slideIndex > 0)
            }
            showEnd={
              viewportBreakpoint !== BreakpointEnum.XLarge &&
              (shouldLoop || slideIndex < itemsToDisplay.length - 1)
            }
            disableScroll
          >
            <Carousel
              id="Tabbed-Media-Carousel"
              onSlideChange={(swiper) => setSlideIndex(swiper.realIndex)}
              analyticsEnabled
              onResize={onResize}
              onInit={() => {
                setIsSwiperInitialized(true);
              }}
              onSwiper={setSwiperInstance}
              spaceBetween={20}
              slidesPerView={1.2}
              navId={navButtonId}
              loop={shouldLoop}
              centeredSlides
              className="whitespace-normal"
              breakpoints={{
                768: {
                  slidesPerView: 1.4,
                  spaceBetween: 20,
                },
                1439: {
                  slidesPerView: 1.4,
                  spaceBetween: 150,
                },
                1750: {
                  slidesPerView: 1.6,
                  spaceBetween: 150,
                },
              }}
            >
              {!isZoomedDesktop && (
                <>
                  <motion.div
                    slot="container-start"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{
                      left: mediaLeftOffset,
                      top: mediaHeight / 2,
                      translateX: '-200%',
                      translateY: '-50%',
                    }}
                    className={classNames('absolute z-10', {
                      hidden: !isNavButtonDisplayed,
                    })}
                  >
                    <NavButton
                      direction={HorizontalDirectionEnum.Left}
                      id={navButtonId}
                      data-testid="CT-135-Tabbed-Media-Carousel-Nav-Left"
                      disabled={!shouldLoop}
                    />
                  </motion.div>

                  <motion.div
                    slot="container-end"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{
                      right: mediaLeftOffset,
                      top: mediaHeight / 2,
                      translateX: '200%',
                      translateY: '-50%',
                    }}
                    className={classNames('absolute z-10', {
                      hidden: !isNavButtonDisplayed,
                    })}
                  >
                    <NavButton
                      direction={HorizontalDirectionEnum.Right}
                      id={navButtonId}
                      data-testid="CT-135-Tabbed-Media-Carousel-Nav-Right"
                      disabled={!shouldLoop}
                    />
                  </motion.div>
                </>
              )}

              {itemsToDisplay &&
                itemsToDisplay.map((card, index) => {
                  const videoCard = card as VideoCard;
                  const imageCard = card as ImageCard;

                  return (
                    <SwiperSlide
                      key={slugify(
                        `${card.title}-${card.description}-${index}`
                      )}
                    >
                      <div
                        className={classNames({
                          'mx-[9%] md:mx-[15%]': !isSwiperInitialized,
                        })}
                        data-testid={`CT-135-Tabbed-Media-Carousel-Card-${index}`}
                      >
                        {'video' in card.media && card.media.video ? (
                          <InViewVideo
                            coverImage={videoCard.media.coverImage}
                            video={videoCard.media.video}
                            autoPlay
                            controls={false}
                            dataTestId={`${videoSlideTestId}-${index}`}
                            hasInVideoPlayButton
                            inVideoPlayButtonPositionClassName="bottom-2.5 left-2.5 md:bottom-5 md:left-5"
                            inVideoPlayButtonTabIndex={
                              index === slideIndex ? 0 : -1
                            }
                          />
                        ) : (
                          <div className="relative">
                            <Image
                              classes="select-none pointer-events-none"
                              image={imageCard.media.image}
                              imageClasses="aspect-video object-cover w-full"
                              lazyLoad={false}
                              dataTestId={`${imageSlideTestId}-${index}`}
                            />
                            {imageCard.pillText && (
                              <div
                                className={classNames(
                                  'absolute bottom-5 left-5 rounded-full bg-black-100 bg-opacity-50 text-white',
                                  isMobile
                                    ? 'body-two px-4 py-1'
                                    : 'body-one px-5 py-2'
                                )}
                                data-testid={`CT-135-Tabbed-Media-Carousel-Zoom-Pill-${index}`}
                              >
                                {imageCard.pillText}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="mt-[86px] flex flex-col gap-5 md:mt-10 md:gap-2.5">
                          {card.title && (
                            <p
                              className="h5"
                              style={{
                                marginRight:
                                  tabsContainerWidth > 0 &&
                                  viewportBreakpoint !== BreakpointEnum.Small
                                    ? tabsContainerWidth + 10
                                    : 0,
                              }}
                              data-testid={`CT-135-Tabbed-Media-Carousel-Card-Title-${index}`}
                            >
                              {card.title}
                            </p>
                          )}
                          {card.description && (
                            <div
                              className="text-grey-450 md:max-w-[80%] lg:max-w-[70%] dark:text-grey-400"
                              dangerouslySetInnerHTML={{
                                __html: card.description,
                              }}
                              data-testid={`CT-135-Tabbed-Media-Carousel-Card-Description-${index}`}
                            />
                          )}
                        </div>
                      </div>
                    </SwiperSlide>
                  );
                })}
            </Carousel>
          </ShadowContainerHorizontal>
        </div>
      )}

      {tabTitles.length > 1 && (
        <div
          style={{
            top:
              mediaHeight +
              50 +
              (isZoomedDesktop && isNavButtonDisplayed ? 80 : 0),
            right:
              viewportBreakpoint !== BreakpointEnum.Small
                ? mediaLeftOffset
                : 'auto',
            left:
              viewportBreakpoint === BreakpointEnum.Small
                ? mediaLeftOffset
                : 'auto',
          }}
          className={classNames(
            '-translate-y-2.5 absolute z-10 w-full max-w-[250px]',
            {
              hidden: !isSwiperInitialized || !viewportBreakpoint,
            }
          )}
        >
          <div className="flex w-full select-none items-center justify-start self-end md:justify-end">
            <ShadowContainerHorizontal
              className="flex w-full md:justify-end"
              hasTabbableNestedItems
            >
              <div className="flex gap-x-4" ref={tabsContainerRef}>
                {tabTitles.map((tabTitle, index) => {
                  const isActive = index === activeTabIndex;

                  if (!tabTitle) return null;

                  return (
                    <Tag
                      className="shrink-0 whitespace-nowrap"
                      key={`tag-tmc-${tabTitle}`}
                      tagType={
                        isActive ? TagTypesEnum.Secondary : TagTypesEnum.Primary
                      }
                      text={tabTitle}
                      pointerStates={!isActive}
                      disabled={isActive}
                      onClick={() => {
                        setActiveTabIndex(index);
                        updateDataLayer({
                          event: 'carouselSelect',
                          link_text: tabTitle,
                        });
                      }}
                    />
                  );
                })}
              </div>
            </ShadowContainerHorizontal>
          </div>
        </div>
      )}
    </section>
  );
};
export default TabbedMediaCarousel;
